import { Box } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { Util } from "./Util";

type SignSide = "positive" | "negative" | "neutral";

export interface SignColorScheme {
	positive: string;
	negative: string;
	neutral?: string;
}

export type SignStyle = "standard" | "accounting";

export interface SignCellConfig {
	colorScheme?: SignColorScheme;
	showPlusPrefix?: boolean;
	signStyle?: SignStyle;
	fontWeight?: "bold" | "medium" | "normal";
	sx?: SxProps<Theme>;
	treatZeroAs?: SignSide;
	formatValue?: (value: number) => string;
}

export const SIGN_COLOR_SCHEMES = {
	GREEN_RED: {
		positive: "success.main",
		negative: "error.main",
		neutral: "text.primary",
	} as SignColorScheme,
} as const;

const toNumber = (value: unknown): number => {
	const num = Number(value);
	return Number.isFinite(num) ? num : 0;
};

const getSignColor = (
	value: number,
	scheme: SignColorScheme,
	treatZeroAs: SignSide
): string => {
	if (value > 0) return scheme.positive;
	if (value < 0) return scheme.negative;

	if (treatZeroAs === "positive") return scheme.positive;
	if (treatZeroAs === "negative") return scheme.negative;

	return scheme.neutral || "text.primary";
};

const buildLabel = (
	value: number,
	showPlusPrefix: boolean,
	formatter: (value: number) => string,
	signStyle: SignStyle
): string => {
	if (signStyle === "accounting") {
		if (value === 0) return "\u2014"; // em dash
		const formatted = formatter(Math.abs(value));
		if (value < 0) return `(${formatted})`;
		return formatted;
	}
	// standard
	const formatted = formatter(value);
	if (showPlusPrefix && value > 0) return `+${formatted}`;
	return formatted;
};

const defaultFormatValue = (value: number): string => Util.formatNumber(value);

export function createSignCell(config: SignCellConfig = {}) {
	const {
		colorScheme = SIGN_COLOR_SCHEMES.GREEN_RED,
		showPlusPrefix = true,
		signStyle = "standard",
		fontWeight = "bold",
		sx = {},
		treatZeroAs = "neutral",
		formatValue = defaultFormatValue,
	} = config;

	return function SignCell({ cell }: { cell: any }) {
		const value = toNumber(cell.getValue());
		const color = getSignColor(value, colorScheme, treatZeroAs);

		return (
			<Box
				component="span"
				sx={{
					color,
					fontWeight,
					...sx,
				}}
			>
				{buildLabel(value, showPlusPrefix, formatValue, signStyle)}
			</Box>
		);
	};
}

export function createDataGridSignCell(config: SignCellConfig = {}) {
	const {
		colorScheme = SIGN_COLOR_SCHEMES.GREEN_RED,
		showPlusPrefix = true,
		signStyle = "standard",
		fontWeight = "medium",
		sx = {},
		treatZeroAs = "neutral",
		formatValue = defaultFormatValue,
	} = config;

	return function DataGridSignCell(params: any) {
		const value = toNumber(params?.value);
		const color = getSignColor(value, colorScheme, treatZeroAs);

		return (
			<Box
				sx={{
					color,
					fontWeight,
					...sx,
				}}
			>
				{buildLabel(value, showPlusPrefix, formatValue, signStyle)}
			</Box>
		);
	};
}
